import { AssignableOrganizationType } from '@/enums/assignableOrganizationsType';
import { CustomFinish, CustomRalFinish } from '@/constants/FinishCodes';
import MaterialCategories from '@/enums/materialCategories';
import PermissionTypes from '@/enums/permissionTypes';
import SeriesTypes from '@/enums/seriesTypes';
import PartitionTypes from '@/enums/partitionTypes';
import Finish from '@/models/finish/finish';
import Material from '@/models/material/material';
import Organization from '@/models/permission/organization';
import Group from '@/models/permission/group';
import Permission from '@/models/permission/permission';
import Partition from '@/models/partition/partition';
import RoomSetting from '@/models/room/roomSetting';
import Series from '@/models/series/series';
import User from '@/models/user/user';
import { vxManager } from '@/store/store';

export type HadrianPermissions = { [key in PermissionTypes]: boolean };

export default class PermissionsHelper {
  public static addToQueue(func: () => void) {
    this.queue.push(func);
  }

  // The permissions validations often depend on certain API calls to be resolved.
  // We delay executing validations with side effects until the required data is loaded in the store.
  public static runQueue() {
    do {
      const func = this.queue.shift();
      if (!func) {
        continue;
      }
      func();
    } while (this.queue.length);
  }

  public static mapPermissions(data: Permission[] = []): HadrianPermissions {
    return Object.fromEntries(
      Object.entries(PermissionTypes).map(([key, value]) => {
        if (isNaN(Number(value)) === false) {
          return [value as PermissionTypes, this.contains(data, value as PermissionTypes)]; // Todo refactor array to use enum names as keys ?
        }
        return [];
      })
    ) as HadrianPermissions;
  }

  public static analyzeUserPermissions(toCheck: PermissionTypes) {
    const userPermissions = vxManager.userModule.getUserPermissions;
    if (!userPermissions) {
      return false;
    }
    const result = userPermissions[toCheck];
    return result;
  }

  public static analyzePermissions(toCheck: PermissionTypes, organizationId: string) {
    const userPermissions = vxManager.userModule.getUserPermissions;
    if (!userPermissions) {
      return false;
    }
    let result = userPermissions[toCheck];
    const orgPermissions = vxManager.permissionsModule.getOrganizationPermissions(organizationId);
    if (orgPermissions) {
      result = orgPermissions[toCheck];
    }

    return result;
  }

  public static hasAccessToMaterial(material: Material, distributorId: string): boolean {
    let hasAccess = true;

    if (!material.isEnabled) {
      hasAccess = false;
    } else if (material.category === MaterialCategories.Phenolic) {
      hasAccess =
        PermissionsHelper.analyzeUserPermissions(PermissionTypes.OverridePhenolicAccess) ||
        PermissionsHelper.analyzePermissions(PermissionTypes.CanAccessPhenolic, distributorId);
    } else if (material.category === MaterialCategories.Plastic) {
      hasAccess =
        PermissionsHelper.analyzeUserPermissions(PermissionTypes.OverridePlasticAccess) ||
        PermissionsHelper.analyzePermissions(PermissionTypes.CanAccessPlastic, distributorId);
    }
    // Other categories
    return hasAccess;
  }

  public static canAccessEssentialRestrictedContent(distributorId: string): boolean {
    return (
      PermissionsHelper.analyzeUserPermissions(PermissionTypes.OverrideEssentialProductsAccess) ||
      PermissionsHelper.analyzePermissions(PermissionTypes.CanAccessEssentialProducts, distributorId)
    );
  }

  public static hasAccessToFinish(finish: Finish, distributorId: string): boolean {
    if (finish.code !== CustomFinish && finish.code !== CustomRalFinish) {
      return true;
    }

    return PermissionsHelper.canAccessEssentialRestrictedContent(distributorId);
  }

  /**
   * Returns true when the material + series + partition combination is blocked for ESS customers.
   *
   * Blocked matrix:
   *   Metal  + Elite Series                        → blocked
   *   Metal  + Elite Max Series                    → blocked
   *   Metal  + Elite Plus + Headrail Ped. Mount    → blocked
   *   Phenolic + Elite Max Series                  → blocked
   */
  public static isEssentialRestrictedCombination(material: Material, series: Series, partition: Partition): boolean {
    const isMetal = material.category === MaterialCategories.Metal;
    const isPhenolic = material.category === MaterialCategories.Phenolic;
    const isPlastic = material.category === MaterialCategories.Plastic;

    if (isPlastic && partition.type === PartitionTypes.Ch) return true;
    if (isMetal && (series.type === SeriesTypes.Elite || series.type === SeriesTypes.EliteMax)) return true;
    if (isMetal && series.type === SeriesTypes.ElitePlus && partition.type === PartitionTypes.Hrpm) return true;
    if (isPhenolic && series.type === SeriesTypes.EliteMax) return true;

    return false;
  }

  /**
   * Returns true when the series is accessible for the given distributor + material context.
   * When material is provided, applies the exact ESS matrix:
   *   Metal    → Elite and Elite Max are blocked
   *   Phenolic → only Elite Max is blocked
   *   Other    → no series restriction
   * When material is omitted, conservatively blocks Elite and Elite Max.
   */
  public static hasAccessToSeries(series: Series, distributorId: string, material?: Material): boolean {
    if (PermissionsHelper.canAccessEssentialRestrictedContent(distributorId)) {
      return true;
    }
    const isMetal = !material || material.category === MaterialCategories.Metal;
    const isPhenolic = material?.category === MaterialCategories.Phenolic;

    if (isMetal && (series.type === SeriesTypes.Elite || series.type === SeriesTypes.EliteMax)) return false;
    if (isPhenolic && series.type === SeriesTypes.EliteMax) return false;

    return true;
  }

  /**
   * Checks material + series + partition access in one call.
   * Use this for room-level guards (e.g. quote / order selection).
   */
  public static hasAccessToRoomSettings(roomSettings: RoomSetting | null | undefined, distributorId: string): boolean {
    if (!roomSettings) {
      return true;
    }
    const { material, series, partition } = roomSettings;

    if (!PermissionsHelper.hasAccessToMaterial(material, distributorId)) {
      return false;
    }

    if (
      PermissionsHelper.isEssentialRestrictedCombination(material, series, partition) &&
      !PermissionsHelper.canAccessEssentialRestrictedContent(distributorId)
    ) {
      return false;
    }

    return true;
  }

  private static queue: Array<() => void> = [];

  private static contains(data: Permission[], type: PermissionTypes): boolean {
    return data.some(p => p.permissionType === type);
  }
}

export const organizationsAssignableToGroup = (
  group: Group,
  distributors: Organization[],
  salesAgencies: Organization[]
): Organization[] => {
  switch (group.assignableOrganizationsType) {
    case AssignableOrganizationType.AllOrganizations:
      return salesAgencies.concat(distributors);
    case AssignableOrganizationType.OnlyDistributors:
      return distributors;
    case AssignableOrganizationType.OnlySalesAgencies:
      return salesAgencies;
    default:
      return [];
  }
};

export const organizationIsAssignableToUser = (
  user: User,
  org: Organization,
  distributors: Organization[],
  salesAgencies: Organization[]
): boolean => {
  switch (user.group.assignableOrganizationsType) {
    case AssignableOrganizationType.AllOrganizations:
      return true;
    case AssignableOrganizationType.OnlyDistributors:
      return !!distributors.find(dist => dist.id === org.id);
    case AssignableOrganizationType.OnlySalesAgencies:
      return !!salesAgencies.find(sa => sa.id === org.id);
    default:
      return false;
  }
};
